import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const NotificationItemSchema = z.object({
  id: UUIDSchema,
  eventId: UUIDSchema,
  eventType: z.string(),
  actorId: UUIDSchema.nullable(),
  notifiableType: z.string().nullable(),
  notifiableId: UUIDSchema.nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
  archivedAt: z.iso.datetime().nullable(),
});

export const ListNotificationsInputSchema = z.object({
  cursor: UUIDSchema.optional(),
  limit: z.int().min(1).max(100).default(20),
  unreadOnly: z.boolean().default(false),
  /**
   * Which archive view to return:
   *   - 'active' (default): rows where archived_at IS NULL — the bell's inbox.
   *   - 'archived': rows where archived_at IS NOT NULL — the history page.
   *   - 'all': both, for unified searches.
   */
  filter: z.enum(["active", "archived", "all"]).default("active"),
});

export const ListNotificationsOutputSchema = z.object({
  notifications: z.array(NotificationItemSchema),
  nextCursor: UUIDSchema.nullable(),
  hasMore: z.boolean(),
});

export const UnreadCountOutputSchema = z.object({
  count: z.int().nonnegative(),
});

export const MarkAsReadInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const MarkAsReadOutputSchema = z.object({
  success: z.boolean(),
});

export const MarkAllAsReadOutputSchema = z.object({
  updatedCount: z.int().nonnegative(),
});

export const ArchiveInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const ArchiveOutputSchema = z.object({
  success: z.boolean(),
});

export const UnarchiveInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const UnarchiveOutputSchema = z.object({
  success: z.boolean(),
});

/**
 * Sender options exposed to workflow authors. The `default_sendgrid`
 * entry is the org-wide verified-sender; `microsoft_graph` entries are
 * the enabled UPNs from the email-ingest configs.
 */
export const NotificationSenderOptionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("default_sendgrid"),
    label: z.string(),
    fromAddress: z.string(),
  }),
  z.object({
    kind: z.literal("microsoft_graph"),
    label: z.string(),
    upn: z.string(),
  }),
]);

export const ListSenderOptionsOutputSchema = z.object({
  options: z.array(NotificationSenderOptionSchema),
});

/**
 * One-off "Notify…" send from an order / discount-group page. Mirrors the
 * Notify workflow step's config (same recipient/channel rules) but fires
 * immediately with nothing persisted beyond the notification rows.
 */
const SendAdHocNotificationShape = z.object({
  recipientUserIds: z.array(UUIDSchema).default([]),
  recipientUserGroupIds: z.array(UUIDSchema).default([]),
  recipientContactIds: z.array(UUIDSchema).default([]),
  channels: z.array(z.enum(["in_app", "email"])).min(1),
  /** Free-form headline. Required unless `emailTemplateId` provides it. */
  title: z.string().trim().min(1).max(200).optional(),
  /** Free-form message. Required unless `emailTemplateId` provides it. */
  body: z.string().trim().min(1).max(4000).optional(),
  senderMailbox: z.string().trim().min(1).optional(),
  /**
   * `builtin:<key>` or an org email-template lineage id. When set without
   * a title/body, the rendered template supplies the in-app title (its
   * subject) and body (its plain text) as well as the email HTML — the
   * notification then reads like a workflow-produced one.
   */
  emailTemplateId: z.string().trim().min(1).optional(),
  emailSubject: z.string().trim().min(1).max(300).optional(),
  subject: z
    .object({
      kind: z.enum(["draft_sales_order", "discount_group"]),
      id: UUIDSchema,
    })
    .optional(),
});
type SendAdHocNotificationShape = z.infer<typeof SendAdHocNotificationShape>;

/**
 * Cross-field rules shared by the API and the Notify dialog's pre-flight:
 * some recipient, contacts need the email channel, email needs a sender, and
 * free-form text is required unless a template supplies it.
 */
export function validateSendAdHocNotificationInput(
  val: SendAdHocNotificationShape,
  ctx: z.RefinementCtx,
): void {
  if (!val.emailTemplateId && (!val.title || !val.body)) {
    ctx.addIssue({
      code: "custom",
      path: ["body"],
      message:
        "A title and message are required unless a template is selected.",
    });
  }
  const hasUsers = val.recipientUserIds.length > 0;
  const hasGroups = val.recipientUserGroupIds.length > 0;
  const hasContacts = val.recipientContactIds.length > 0;
  if (!hasUsers && !hasGroups && !hasContacts) {
    ctx.addIssue({
      code: "custom",
      path: ["recipientUserIds"],
      message:
        "At least one recipient user, user group, or address-book contact is required.",
    });
  }
  if (
    hasContacts &&
    !hasUsers &&
    !hasGroups &&
    !val.channels.includes("email")
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["recipientContactIds"],
      message:
        "Address-book contacts only receive email — enable the email channel or add user recipients.",
    });
  }
  if (val.channels.includes("email") && !val.senderMailbox) {
    ctx.addIssue({
      code: "custom",
      path: ["senderMailbox"],
      message: "A sender mailbox is required when email is selected.",
    });
  }
}

export const SendAdHocNotificationInputSchema =
  SendAdHocNotificationShape.superRefine(validateSendAdHocNotificationInput);

export const SendAdHocNotificationOutputSchema = z.object({
  recipientCount: z.int().nonnegative(),
  emailRecipientCount: z.int().nonnegative(),
  emailSent: z.boolean(),
});

export const notificationsContracts = {
  sendAdHoc: oc
    .input(SendAdHocNotificationInputSchema)
    .output(SendAdHocNotificationOutputSchema)
    .route({
      method: "POST",
      summary: "Send a notification to people",
      description:
        "Immediately notifies chosen users, user groups, or contacts in-app and/or by email, with free text or an email template, optionally about an order or discount group.",
    }),

  list: oc
    .input(ListNotificationsInputSchema)
    .output(ListNotificationsOutputSchema)
    .route({
      method: "GET",
      summary: "List my notifications",
      description:
        "Lists the current user's notifications newest first with cursor paging. Filter to unread, and to active, archived, or all.",
    }),

  unreadCount: oc.output(UnreadCountOutputSchema).route({
    method: "GET",
    summary: "Count my unread notifications",
    description:
      "Returns the number of unread notifications for the current user.",
  }),

  markAsRead: oc
    .input(MarkAsReadInputSchema)
    .output(MarkAsReadOutputSchema)
    .route({
      method: "POST",
      summary: "Mark a notification read",
      description: "Marks one of the current user's notifications as read.",
    }),

  markAllAsRead: oc.output(MarkAllAsReadOutputSchema).route({
    method: "POST",
    summary: "Mark all notifications read",
    description:
      "Marks every unread notification for the current user as read and returns how many changed.",
  }),

  archive: oc.input(ArchiveInputSchema).output(ArchiveOutputSchema).route({
    method: "POST",
    summary: "Archive a notification",
    description:
      "Moves one of the current user's notifications out of the inbox into history.",
  }),

  unarchive: oc
    .input(UnarchiveInputSchema)
    .output(UnarchiveOutputSchema)
    .route({
      method: "POST",
      summary: "Unarchive a notification",
      description:
        "Restores an archived notification to the current user's inbox.",
    }),

  listSenderOptions: oc.output(ListSenderOptionsOutputSchema).route({
    method: "GET",
    summary: "List notification email senders",
    description:
      "Lists the addresses that notification emails can be sent from: the default system sender and the organization's connected mailboxes.",
  }),
} as const;

export type NotificationSenderOption = z.infer<
  typeof NotificationSenderOptionSchema
>;
export type ListSenderOptionsOutput = z.infer<
  typeof ListSenderOptionsOutputSchema
>;
export type NotificationItem = z.infer<typeof NotificationItemSchema>;
export type ListNotificationsInput = z.infer<
  typeof ListNotificationsInputSchema
>;
export type ListNotificationsOutput = z.infer<
  typeof ListNotificationsOutputSchema
>;
export type SendAdHocNotificationInput = z.input<
  typeof SendAdHocNotificationInputSchema
>;
export type SendAdHocNotificationOutput = z.infer<
  typeof SendAdHocNotificationOutputSchema
>;
